/**
 * Who the owner field will accept, when something has already proposed an answer (WWM-17508).
 *
 * Two places ask this -- the AddLeadPage Assignment section, and OwnerSelect, which decides what
 * goes in the dropdown. They had the same rule written out separately and no test on either copy,
 * which is how the territory case came to behave differently in each.
 *
 * The distinction that matters:
 *
 *   An assignment RULE is a business policy. It narrows the field and locks it, and people with
 *   override permission may ignore it. That is unchanged.
 *
 *   A D2D TERRITORY is a suggestion. The lead's coordinates fall inside somebody's patch, so that
 *   person is pre-filled -- but a rep standing at the door may know better, and the earlier
 *   behaviour (lock whenever the patch had exactly one owner) gave them no way to say so short of
 *   clearing the address. Override permission is not consulted, because a suggestion is not
 *   something only privileged users may decline.
 */

type OwnerFieldRule = {
  isTerritoryDerived?: boolean
  approvedOwnerIds?: unknown[] | null
  approvedSalesTeamIds?: unknown[] | null
} | null

/** Whether the owner and sales-team fields should be read-only. */
export function isOwnerFieldLocked(args: {
  /** The caller disabled the whole section for its own reasons; nothing here overrides that. */
  explicitlyDisabled: boolean
  /** Null when nothing has proposed an assignment, which leaves the field free. */
  rule: OwnerFieldRule
  /** Whether the user may ignore an assignment rule. Irrelevant to territories. */
  canOverrideAutomatedAssignment: boolean
}): boolean {
  const { explicitlyDisabled, rule, canOverrideAutomatedAssignment } = args

  if (explicitlyDisabled) return true
  if (!rule) return false
  if (rule.isTerritoryDerived) return false

  const approvedCount = rule.approvedOwnerIds?.length ?? 0

  // No approved owners at all is the server saying UNCONSTRAINED, not "exactly nobody".
  // SearchMatchingRule sends ApprovedOwnerIds as null when the matching rule names no owners,
  // and locking on that left a rep without override permission holding a field that is
  // required, empty and read-only -- the lead could not be saved at all.
  if (approvedCount === 0) return false

  // Several eligible owners means there is a real choice to make, and nobody but the person
  // filling the form can make it. Locking here is also unsubmittable in practice: nothing
  // pre-selects when the candidates are ambiguous, so the field would be blank, required AND
  // read-only. Overlapping territories made this reachable -- a point inside two of them now
  // offers both rules' owners.
  if (approvedCount > 1) return false

  return !canOverrideAutomatedAssignment
}

/**
 * Whether the SALES TEAM field should be read-only.
 *
 * Its own predicate, not the owner one. The owner field unlocks when a rule approves several
 * owners, because choosing between people is a choice only the person filling the form can make
 * -- but the number of approved OWNERS says nothing about whether the rule pinned a single sales
 * TEAM, and routing the team through the owner policy dropped the team restriction in two cases a
 * conventional rule reaches without any territory being involved: a rule naming one team and no
 * owners at all (ApprovedOwnerIds arrives null), and a rule naming one team and two owners. In
 * both, a rep without override permission could move the lead off the team the rule dictates.
 *
 * So this mirrors exactly what master gated the field on -- one approved team, and you may not
 * override -- with the territory carve-out the rest of this module applies: a territory suggests
 * a team, it does not dictate one.
 */
export function isSalesTeamFieldLocked(args: {
  explicitlyDisabled: boolean
  rule: OwnerFieldRule
  canOverrideAutomatedAssignment: boolean
}): boolean {
  const { explicitlyDisabled, rule, canOverrideAutomatedAssignment } = args

  if (explicitlyDisabled) return true
  if (!rule) return false
  if (rule.isTerritoryDerived) return false

  if ((rule.approvedSalesTeamIds?.length ?? 0) !== 1) return false

  return !canOverrideAutomatedAssignment
}

/**
 * Whether the SALES FUNNEL and LEAD STATUS fields should be read-only.
 *
 * Deliberately not isOwnerFieldLocked. That one unlocks when several owners are eligible, because
 * a choice between people is a choice only the person filling the form can make -- but a rule that
 * approves two owners still dictates one funnel and one status, so the same reasoning does not
 * carry across. Routing these fields through the owner policy handed a rep without override
 * permission a funnel and status that had been read-only.
 *
 * A TERRITORY does not lock them either, and that part is not an oversight: a territory is a
 * suggestion about WHO, it carries no funnel or status of its own, and it has no business locking
 * fields it says nothing about.
 */
export function areAssignmentFieldsLocked(args: {
  explicitlyDisabled: boolean
  rule: OwnerFieldRule
  canOverrideAutomatedAssignment: boolean
}): boolean {
  const { explicitlyDisabled, rule, canOverrideAutomatedAssignment } = args

  if (explicitlyDisabled) return true
  if (!rule) return false
  if (rule.isTerritoryDerived) return false

  return !canOverrideAutomatedAssignment
}

/**
 * Whether the dropdown should be narrowed to the proposed owners.
 *
 * False for a territory even though it supplies owner ids: narrowing to them is what made the
 * single-owner case a dropdown with one entry, which is not a choice.
 */
export function ruleNarrowsOwnerOptions(args: {
  rule: OwnerFieldRule
  canOverrideAutomatedAssignment: boolean
}): boolean {
  const { rule, canOverrideAutomatedAssignment } = args

  if (!rule) return false
  if (!rule.approvedOwnerIds?.length) return false
  if (rule.isTerritoryDerived) return false

  // Several eligible owners: show exactly those, whoever is looking. This is the overlapping
  // territory case, where the candidates ARE the answer to "who may own this" -- dropping them
  // into the whole sales team would bury the two people who actually have a claim, and an admin
  // needs that answer more than anyone, not less.
  if (rule.approvedOwnerIds.length > 1) return true

  return !canOverrideAutomatedAssignment
}

/**
 * Which sales team the form should start on.
 *
 * One approved team is the easy case. Several means the eligible owners span teams -- two
 * overlapping territories belonging to different ones -- and the form used to clear the field
 * rather than choose. That looked harmless and was not: OwnerSelect builds its options for the
 * SELECTED team and returns an empty list when there is none, so clearing the team emptied the
 * owner dropdown entirely, on exactly the lead that most needed a choice offered.
 *
 * The first approved team is the one belonging to the rule that actually fired. The server
 * unions the candidates in Order with the winner first, which
 * TheFirstApprovedTeamBelongsToTheWinningRule pins on that side. Picking any team is enough to
 * populate the list, because an eligible owner from another team is added back by name, and
 * choosing them moves the team to theirs.
 */
export function initialSalesTeamId(
  approvedSalesTeamIds: (number | string)[] | null | undefined,
): string {
  if (!approvedSalesTeamIds?.length) return ''
  return String(approvedSalesTeamIds[0])
}

/**
 * The sales team a newly chosen owner brings with them, or null when nothing should move.
 *
 * Eligible owners can span teams -- two overlapping territories may belong to different ones --
 * and leaving the team behind files the lead under a team its owner is not on. Returns null when
 * the owner is unknown or already on the selected team, so the ordinary single-team case never
 * touches the field.
 */
export function salesTeamIdForOwner(
  users: { userId: string; salesTeamId?: number | string | null }[],
  ownerId: string | null | undefined,
  currentSalesTeamId: string | number | null | undefined,
  approvedSalesTeamIds?: (number | string)[] | null,
): string | null {
  if (!ownerId) return null

  // A person can sit on several sales teams -- five, in this tenant -- and this list holds one
  // row per membership. Taking the first match would move the lead to whichever row happened to
  // come back first, which is arbitrary and usually wrong.
  const teams = users
    .filter((u) => u.userId === ownerId && u.salesTeamId)
    .map((u) => String(u.salesTeamId))

  if (!teams.length) return null

  // Already on one of their teams: nothing to move, whichever of them it is.
  if (teams.includes(String(currentSalesTeamId ?? ''))) return null

  // Prefer a team the assignment actually approved, so the move lands somewhere the rule or
  // territory nominated rather than on an unrelated team the person also happens to be on.
  const approved = (approvedSalesTeamIds ?? []).map(String)
  return teams.find((t) => approved.includes(t)) ?? teams[0]
}

/**
 * Which owner the form should start on.
 *
 * The first approved owner, by the same contract as initialSalesTeamId: the server unions the
 * candidates in Order, so index 0 belongs to the rule that actually fired.
 *
 * Several candidates used to leave this blank on the grounds that the user should choose. That
 * was inconsistent once the team began defaulting to the winning rule's -- the form picked a
 * default for one field and refused for the other -- and it left a REQUIRED field empty on the
 * common case where the winner is simply right. Pre-filling is not deciding: the field stays
 * unlocked whenever there are several candidates, and the dropdown lists them all.
 */
export function initialOwnerId(
  approvedOwnerIds: (string | null)[] | null | undefined,
): string {
  if (!approvedOwnerIds?.length) return ''
  return String(approvedOwnerIds[0] ?? '')
}
